/**
 * ⭐ 申请解锁弹窗。
 *
 * 理由必填且**不少于 20 字**——校验阈值复用 `@atlaspm/shared` 的
 * `UNLOCK_REASON_MIN_LENGTH`，与后端常量保持同一口径。
 */

import { useEffect, useRef, useState } from 'react';
import Button from '../../components/Button';
import { ApiError } from '../../api/client';
import { UNLOCK_WINDOW_HOURS, UNLOCK_REASON_MIN_LENGTH } from '@atlaspm/shared';
import { useRequestUnlock } from './useTaskDetail';

export interface UnlockDialogProps {
  taskId: string;
  taskName: string;
  open: boolean;
  onClose(): void;
  onSubmitted?(): void;
}

export default function UnlockDialog({
  taskId,
  taskName,
  open,
  onClose,
  onSubmitted,
}: UnlockDialogProps) {
  const [reason, setReason] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const mutation = useRequestUnlock(taskId);

  useEffect(() => {
    if (open) {
      setReason('');
      setDone(false);
      setError(null);
      mutation.reset();
      setTimeout(() => taRef.current?.focus(), 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const count = reason.trim().length;
  const valid = count >= UNLOCK_REASON_MIN_LENGTH;

  const submit = () => {
    setError(null);
    if (!valid) return;
    mutation.mutate(reason.trim(), {
      onSuccess: () => {
        setDone(true);
        onSubmitted?.();
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          // 后端对过短理由抛的是普通 Error（HTTP 500），前端已本地拦截
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : '提交失败');
        }
      },
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-box__title">🔓 申请解锁 · {taskName}</div>

        {done ? (
          <div className="modal-box__body">
            <div className="unlock-done">
              ✅ 已提交解锁申请，等待审批。
              <div className="unlock-done__hint">
                审批通过后进入 <b>{UNLOCK_WINDOW_HOURS} 小时</b>编辑窗口；
                修改完成后须重新走完整审批流，生成新冻结版本。
              </div>
            </div>
            <div className="modal-box__foot">
              <Button variant="primary" onClick={onClose}>
                知道了
              </Button>
            </div>
          </div>
        ) : (
          <div className="modal-box__body">
            <label className="modal-field">
              <span className="modal-field__label">
                解锁理由（必填，至少 {UNLOCK_REASON_MIN_LENGTH} 字）
              </span>
              <textarea
                ref={taRef}
                rows={4}
                value={reason}
                placeholder="例如：客户要求补充第三方检测报告，原附件版本有误，需替换后重新报审"
                onChange={(e) => setReason(e.target.value)}
              />
              <span className={['modal-field__count', valid ? 'is-ok' : ''].join(' ')}>
                {count} / {UNLOCK_REASON_MIN_LENGTH} 字
              </span>
            </label>

            <div className="unlock-explain">
              <b>解锁后会发生什么：</b>
              <ol>
                <li>进入限时编辑窗口（默认 {UNLOCK_WINDOW_HOURS} 小时），超时自动回冻</li>
                <li>修改完成后必须重新走完整审批流，生成新冻结版本</li>
                <li>旧版本与冻结快照永久保留，可版本对比</li>
                <li>系统自动通知原审批人，并附上你的变更理由</li>
              </ol>
            </div>

            {error && <div className="cf-error">⚠ {error}</div>}

            <div className="modal-box__foot">
              <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
                取消
              </Button>
              <Button
                variant="danger"
                onClick={submit}
                disabled={!valid || mutation.isPending}
                title={!valid ? `理由至少 ${UNLOCK_REASON_MIN_LENGTH} 字` : undefined}
              >
                {mutation.isPending ? '提交中…' : '提交申请'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
